import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  muscleKey,
  weeklyTargetDegradedMax,
  weeklyTargetRowLabel,
  type CatalogMuscleSlug,
  type UnitsPreference,
  type WeeklyTargetsData,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { Button } from "../primitives/Button";
import { DisclosureCard } from "../primitives/DisclosureCard";
import { StatPair } from "../primitives/StatPair";
import { TargetMeterRow } from "../primitives/TargetMeterRow";

// CO-7 R4 / ME-3 — the coach's read-only weekly-targets panel inside
// <AthleteDetail>. It is the athlete's OWN Home meter (Home/WeeklyTargetsCard),
// re-framed for the coach: the same <DisclosureCard> + <TargetMeterRow> idiom
// over the same computeWeeklyTargets data, but the expanded body's drill-in
// ("View progress") is replaced by an «Edit» affordance that opens
// <TargetsEditor>.
//
// The host scores the data (AthleteDetail already holds results + catalog +
// the athlete's AthleteTargets + prescription) and passes it in — no fetch
// here, exactly like the Home card. ME-3: the targets are the athlete's own, so
// the panel no longer needs a pinned program; `data` is null only when the
// athlete has no trained muscles at all (nothing to score OR author).
//
// «Edit» is gated on the athlete's `write:targets` grant BEFORE the tap
// (edgeHasGrant, resolved by the host from its coach-edges): a coach without the
// grant sees the targets read-only and an honest "the athlete hasn't granted
// edits" line, rather than discovering the 403 only after editing. Rows are
// composed from <TargetMeterRow> directly (the plain Home row shape, no trend
// bars — MuscleCoverageSection's trend variant is a different surface).

export type CoachTargetsPanelProps = {
  theme: UiTheme;
  /** The athlete's scored weekly targets (computeWeeklyTargets).
   *  Null → nothing to show; the panel renders nothing. */
  data: WeeklyTargetsData | null;
  units: UnitsPreference;
  locale: string;
  /** Resolve an exercise slug → display name (exercise-axis rows). */
  resolveExerciseName: (slug: string) => string;
  /** The athlete has granted write:targets (edgeHasGrant). Gates «Edit». */
  canEdit: boolean;
  /** Open the athlete's targets editor. */
  onEdit: () => void;
};

export function CoachTargetsPanel({
  theme,
  data,
  units,
  locale,
  resolveExerciseName,
  canEdit,
  onEdit,
}: CoachTargetsPanelProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  if (data === null) return null;

  const scored = data.mode === "scored";
  const resolveMuscle = (slug: string): string =>
    t(muscleKey(slug as CatalogMuscleSlug));
  const degradedMax = weeklyTargetDegradedMax(data.rows);

  // The heading is what this IS: the athlete's own weekly targets. NOT the
  // pinned block name — targets belong to the athlete now (ME-3), and titling
  // the panel with the program read as "the program's targets", the exact
  // confusion this migration removes. The block's prescription still shows, as
  // the 2nd reference mark on each row, not in the header.
  const label = t("coach.targets.eyebrow", { defaultValue: "Weekly targets" });
  const onTargetA11y = scored
    ? t("mobile.home.targetsSummary", {
        on: data.onTargetCount,
        total: data.targetCount,
        defaultValue: "{{on}} of {{total}} on target",
      })
    : "";
  const a11yLabel = [label, onTargetA11y].filter(Boolean).join(". ");

  return (
    <DisclosureCard
      theme={theme}
      accessibilityLabel={a11yLabel}
      header={
        <>
          <Text style={styles.title} numberOfLines={1}>
            {label}
          </Text>
          {scored ? (
            <View style={styles.statsRow}>
              <StatPair
                theme={theme}
                value={`${data.onTargetCount}/${data.targetCount}`}
                label={t("mobile.home.targetsZoneOnTarget", {
                  defaultValue: "on target",
                })}
              />
            </View>
          ) : null}
        </>
      }
    >
      {data.rows.map((row, i) => (
        <View
          key={`${row.axis}:${row.key}:${row.metric}`}
          style={[styles.row, i === data.rows.length - 1 && styles.rowLast]}
        >
          <TargetMeterRow
            theme={theme}
            row={row}
            label={weeklyTargetRowLabel(row, resolveExerciseName, resolveMuscle)}
            units={units}
            locale={locale}
            degradedMax={degradedMax}
          />
        </View>
      ))}

      {!scored ? (
        <Text style={styles.note}>
          {t("coach.targets.degradedHint", {
            defaultValue:
              "This athlete has no weekly targets yet. Set some to score their week against a goal.",
          })}
        </Text>
      ) : null}

      {canEdit ? (
        <View style={styles.actionRow}>
          <Button
            theme={theme}
            variant="secondary"
            icon="edit"
            label={t("coach.targets.edit", { defaultValue: "Edit targets" })}
            onPress={onEdit}
          />
        </View>
      ) : (
        <Text style={styles.note}>
          {t("coach.targets.noGrant", {
            defaultValue:
              "Read-only — the athlete hasn't granted you target edits.",
          })}
        </Text>
      )}
    </DisclosureCard>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    title: { color: p.ink, fontSize: ty.sizes.md, fontWeight: "600" },
    statsRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s6,
      marginTop: s.s2,
    },
    row: {
      paddingTop: s.s3,
      paddingBottom: s.s2,
      borderBottomWidth: 1,
      borderBottomColor: p.border,
    },
    // The action row / note below supplies the closing gap — the last meter row
    // shouldn't also draw a rule right above it.
    rowLast: { borderBottomWidth: 0 },
    // Actionable statements (degradedHint / noGrant), not decorative captions —
    // one ink step up from a muted caption so the coach reads the "why".
    note: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      paddingTop: s.s3,
      lineHeight: ty.sizes.sm * 1.4,
    },
    actionRow: { paddingTop: s.s3, alignItems: "flex-start" },
  });
}
